/**
 * Executions-By-Status slice — server state + UI form state.
 *
 * Owns the full lifecycle: user-editable form inputs (baseForm, dateRange,
 * propertyQuery), the committed snapshot that drives the fetch, pagination
 * position, and the API response. Colocation lets the page component
 * read/write via useAppSelector/useAppDispatch without local useState —
 * consistent with the codebase's Redux-for-state-management pattern.
 */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { DateRangePickerProps } from '@cloudscape-design/components/date-range-picker';
import type { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import { beaconApi } from 'src/services/beaconApi';
import type { RootState } from 'src/store/store';

/** ExecutionSummary — ExecutionResponse without statusTimeline (list view). */
export interface ExecutionSummary {
  id: string;
  parentWorkflowId: string | null;
  paymentIds: string[] | null;
  executionType: string;
  executionTags: string[] | null;
  paymentType: string | null;
  pluginName: string | null;
  dataSource: string | null;
  beaconExecutionStatus: string;
  beaconExecutionState: string | null;
  firstStatusAt: string | null;
  totalLatencyMs: number | null;
  errorInfo: string | null;
  createdAt: string;
  updatedAt: string;
}

export type SortField = 'UPDATED_AT_DESC' | 'UPDATED_AT_ASC';

export interface BaseFormState {
  status: string;
  state: string;
  dataSource: string;
  sortBy: SortField;
}

export interface GetExecutionsByStatusRequest {
  executionQuery: {
    status: string;
    state: string;
    dataSource: string;
    additionalFilters?: Record<string, string>;
  };
  fromDate: number;
  toDate: number;
  sortBy?: SortField;
  pagination?: { pageNumber: number; pageSize: number };
}

export interface GetExecutionsByStatusResponse {
  executions: ExecutionSummary[];
  totalExecutionsCount: number;
}

/** Snapshot of form inputs at the time of the last Search click. */
export interface CommittedSnapshot {
  baseForm: BaseFormState;
  dateRange: DateRangePickerProps.Value | null;
  propertyQuery: PropertyFilterProps.Query;
}

interface State {
  // Server state
  data: GetExecutionsByStatusResponse | null;
  loading: boolean;
  error: string | null;
  currentRequestId: string | undefined;
  // UI state — form inputs
  baseForm: BaseFormState;
  dateRange: DateRangePickerProps.Value | null;
  propertyQuery: PropertyFilterProps.Query;
  submitted: boolean;
  currentPage: number;
  committed: CommittedSnapshot | null;
}

const INITIAL_BASE_FORM: BaseFormState = {
  status: '', state: '', dataSource: '', sortBy: 'UPDATED_AT_DESC',
};

/** Rolling last-24-hour default — a relative range so the window slides forward
 *  with the wall clock instead of drifting to a stale absolute yesterday-to-today. */
const INITIAL_DATE_RANGE: DateRangePickerProps.RelativeValue = {
  type: 'relative', amount: 24, unit: 'hour', key: 'previous-24-hours',
};

const INITIAL_PROPERTY_QUERY: PropertyFilterProps.Query = { operation: 'and', tokens: [] };

const initialState: State = {
  data: null,
  loading: false,
  error: null,
  currentRequestId: undefined,
  baseForm: INITIAL_BASE_FORM,
  dateRange: INITIAL_DATE_RANGE,
  propertyQuery: INITIAL_PROPERTY_QUERY,
  submitted: false,
  currentPage: 1,
  committed: null,
};

export const fetchExecutionsByStatus = createAsyncThunk<
  GetExecutionsByStatusResponse,
  GetExecutionsByStatusRequest,
  { state: RootState; rejectValue: string }
>('executionsByStatus/fetch', async (request, { getState, rejectWithValue }) => {
  try {
    return await beaconApi.getExecutionsByStatus(request, getState().env.apiBase);
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch executions');
  }
});

const slice = createSlice({
  name: 'executionsByStatus',
  initialState,
  reducers: {
    // Required top-form fields — do NOT auto-commit; user must click Search.
    setStatus: (state, action: PayloadAction<string>) => {
      state.baseForm.status = action.payload;
    },
    setStateFilter: (state, action: PayloadAction<string>) => {
      state.baseForm.state = action.payload;
    },
    setDataSource: (state, action: PayloadAction<string>) => {
      state.baseForm.dataSource = action.payload;
    },
    // Refinement setters — auto-commit when a Search has already run so
    // the results refresh immediately (no re-click required). The auto-
    // commit path mutates `committed` from the SAME slice state — so
    // unsubmitted top-form edits (status/state/dataSource) don't leak
    // into the next request because we don't touch committed.baseForm.
    setDateRange: (state, action: PayloadAction<DateRangePickerProps.Value | null>) => {
      state.dateRange = action.payload;
      if (state.committed) {
        state.currentPage = 1;
        state.committed.dateRange = action.payload;
      }
    },
    setPropertyQuery: (state, action: PayloadAction<PropertyFilterProps.Query>) => {
      // Cast to `any` — Cloudscape Query.tokens is readonly, Immer requires mutable.
      state.propertyQuery = action.payload as any;
      if (state.committed) {
        state.currentPage = 1;
        // Cast to `any` — same reason as above.
        state.committed.propertyQuery = action.payload as any;
      }
    },
    setSortBy: (state, action: PayloadAction<SortField>) => {
      state.baseForm.sortBy = action.payload;
      if (state.committed) {
        state.currentPage = 1;
        state.committed.baseForm.sortBy = action.payload;
      }
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    /** Snapshot current inputs → committed. Fetch driver useEffect fires. */
    commit: (state) => {
      state.submitted = true;
      state.currentPage = 1;
      state.committed = {
        baseForm: { ...state.baseForm },
        dateRange: state.dateRange,
        propertyQuery: state.propertyQuery,
      };
    },
    /** Marks the form as submitted so validation errors render inline. */
    markSubmitted: (state) => {
      state.submitted = true;
    },
    /** Re-triggers the same fetch — used by the Retry button on the error alert. */
    retry: (state) => {
      if (state.committed) {
        state.error = null;
        state.committed = { ...state.committed };
      }
    },
    /** Full reset — Clear button. */
    resetAll: () => initialState,
    /** Server-state reset. Called on unmount so a return visit starts clean. */
    clearExecutionsByStatus: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
      state.currentRequestId = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExecutionsByStatus.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchExecutionsByStatus.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return; // stale
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchExecutionsByStatus.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return; // stale
        state.loading = false;
        state.error = action.payload || 'Failed to fetch executions';
      });
  },
});

export const {
  setStatus,
  setStateFilter,
  setDataSource,
  setDateRange,
  setPropertyQuery,
  setSortBy,
  setCurrentPage,
  commit,
  markSubmitted,
  retry,
  resetAll,
  clearExecutionsByStatus,
} = slice.actions;

export default slice.reducer;
